import React, { useState } from 'react';
import axios from 'axios';
import { getFileUrl } from '../../utils/fileUtil';

const isImage = (p) => /\.(jpg|jpeg|png|gif|webp)$/i.test(p || '');

const fileIcon = (name = '') => {
  const n = name.toLowerCase();
  if (/\.pdf$/.test(n)) return { label: 'PDF', color: 'bg-red-500' };
  if (/\.(doc|docx)$/.test(n)) return { label: 'DOC', color: 'bg-blue-500' };
  if (/\.(xls|xlsx|csv)$/.test(n)) return { label: 'XLS', color: 'bg-green-600' };
  if (/\.(ppt|pptx)$/.test(n)) return { label: 'PPT', color: 'bg-orange-500' };
  if (/\.(txt|md)$/.test(n)) return { label: 'TXT', color: 'bg-gray-500' };
  if (/\.(zip|rar|7z|tar|gz)$/.test(n)) return { label: 'ZIP', color: 'bg-yellow-600' };
  if (/\.(mp4|mov|avi|webm)$/.test(n)) return { label: 'VID', color: 'bg-purple-500' };
  if (/\.(mp3|wav|m4a|ogg)$/.test(n)) return { label: 'AUD', color: 'bg-pink-500' };
  const ext = n.includes('.') ? n.split('.').pop().slice(0, 4).toUpperCase() : 'FILE';
  return { label: ext || 'FILE', color: 'bg-gray-400' };
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const AttachmentExplorer = ({ card, token, onClose, onPreview, onCardUpdated }) => {
  const [selected, setSelected] = useState([]); // attachment ids
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');

  const attachments = card?.attachments || [];
  const allSelected = attachments.length > 0 && selected.length === attachments.length;

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : attachments.map(a => a._id));
  };

  const downloadOne = async (att) => {
    setBusy(true); setBusyMsg(`Downloading ${att.name}...`);
    try {
      const res = await axios.get(`/api/cards/${card._id}/attachments/${att._id}/download`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });
      saveBlob(res.data, att.name || 'file');
    } catch (err) {
      console.error(err);
      alert('Download failed.');
    } finally {
      setBusy(false); setBusyMsg('');
    }
  };

  const deleteOne = async (att) => {
    if (!window.confirm(`Delete "${att.name}"?`)) return;
    setBusy(true); setBusyMsg('Deleting...');
    try {
      const res = await axios.delete(`/api/cards/${card._id}/attachments/${att._id}`, {
        headers: { 'x-auth-token': token }
      });
      setSelected((prev) => prev.filter(x => x !== att._id));
      onCardUpdated(res.data);
    } catch (err) {
      console.error(err);
      alert('Delete failed.');
    } finally {
      setBusy(false); setBusyMsg('');
    }
  };

  const bulkDelete = async (ids, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm(`Delete ${ids.length} file${ids.length > 1 ? 's' : ''} from this card?`)) return;
    setBusy(true); setBusyMsg('Deleting...');
    try {
      const res = await axios.post(`/api/cards/${card._id}/attachments/bulk-delete`, { attachmentIds: ids }, {
        headers: { 'x-auth-token': token }
      });
      setSelected([]);
      onCardUpdated(res.data.card);
    } catch (err) {
      console.error(err);
      alert('Delete failed.');
    } finally {
      setBusy(false); setBusyMsg('');
    }
  };

  const downloadZip = async () => {
    const ids = [...selected];
    setBusy(true); setBusyMsg(`Zipping ${ids.length} file${ids.length > 1 ? 's' : ''}...`);
    try {
      const res = await axios.post(`/api/cards/${card._id}/attachments/zip`, { attachmentIds: ids }, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });
      const zipName = `${(card.title || 'card').replace(/[^a-zA-Z0-9-_ ]/g, '').trim() || 'card'}-files.zip`;
      saveBlob(res.data, zipName);
      setBusy(false); setBusyMsg('');
      // Offer cleanup after a successful zip download
      if (window.confirm(`Downloaded ${ids.length} file${ids.length > 1 ? 's' : ''} as a zip. Delete them from the card now?`)) {
        await bulkDelete(ids, true);
      }
    } catch (err) {
      console.error(err);
      alert('Zip download failed.');
      setBusy(false); setBusyMsg('');
    }
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-gray-100">Files</h3>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tighter truncate">
              {card.title} — {attachments.length} file{attachments.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/60">
          <label className="flex items-center space-x-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              disabled={attachments.length === 0}
              className="h-4 w-4 rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
            />
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {selected.length > 0 ? `${selected.length} selected` : 'Select all'}
            </span>
          </label>
          <div className="flex items-center space-x-2">
            {busyMsg && <span className="text-[11px] text-gray-500 dark:text-gray-400 animate-pulse mr-1">{busyMsg}</span>}
            <button
              onClick={downloadZip}
              disabled={busy || selected.length === 0}
              className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider bg-primary-blue text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Download Zip
            </button>
            <button
              onClick={() => bulkDelete(selected)}
              disabled={busy || selected.length === 0}
              className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          </div>
        </div>

        {/* File grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {attachments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No files on this card.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {attachments.map((att) => {
                const image = isImage(att.path);
                const url = att.signedUrl || getFileUrl(att.path);
                const icon = fileIcon(att.name);
                const checked = selected.includes(att._id);
                return (
                  <div
                    key={att._id}
                    className={`relative group rounded-xl border-2 overflow-hidden transition-all ${checked ? 'border-primary-blue ring-2 ring-primary-blue/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'}`}
                  >
                    {/* Checkbox */}
                    <div className={`absolute top-2 left-2 z-10 transition-opacity ${checked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(att._id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-blue focus:ring-primary-blue shadow"
                      />
                    </div>

                    {/* Thumbnail / icon — click to preview */}
                    <div
                      className="h-24 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer"
                      onClick={() => (checked || selected.length > 0 ? toggle(att._id) : onPreview(att))}
                      title={selected.length > 0 ? 'Toggle selection' : 'Open preview'}
                    >
                      {image ? (
                        <img src={url} alt={att.name} className="h-full w-full object-cover" />
                      ) : (
                        <span className={`${icon.color} text-white text-[10px] font-black px-2.5 py-1.5 rounded-md tracking-widest`}>{icon.label}</span>
                      )}
                    </div>

                    {/* Name + date */}
                    <div className="px-2.5 py-2 bg-white dark:bg-gray-900">
                      <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200 truncate" title={att.name}>{att.name || 'Unnamed File'}</p>
                      <p className="text-[9px] text-gray-400 uppercase tracking-tight">
                        {att.createdAt ? new Date(att.createdAt).toLocaleDateString() : ''}
                      </p>
                    </div>

                    {/* Hover actions */}
                    <div className="absolute bottom-9 right-1.5 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onPreview(att)}
                        disabled={busy}
                        className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm"
                        title="Open preview"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      </button>
                      <button
                        onClick={() => downloadOne(att)}
                        disabled={busy}
                        className="p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm"
                        title="Download"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </button>
                      <button
                        onClick={() => deleteOne(att)}
                        disabled={busy}
                        className="p-1.5 rounded-md bg-red-500/80 text-white hover:bg-red-600 backdrop-blur-sm"
                        title="Delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentExplorer;
